import { Box, FormControl, Grid, InputAdornment, makeStyles, Modal, styled, TextField, Theme, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { BlackExitIcon, CheckedRadio, PdfIcon, UncheckRadio } from '../../../assets/SvgIcons'
import { CustomeButton, CustomeModalDropDown } from '../../../lib/formik/NewFormInputs'
import { useFormik } from 'formik'
import * as Yup from 'yup';
import { GetMandateCreationDetailsResponseBody, GetMandateRegisteredResponseBody, MandateCreation } from '../../../redux-store/types/investorProfile'
import { useDispatch, useSelector } from 'react-redux'
import { getMandateCreationDetails, getMandateRegistered, } from '../../../redux-store/actions/investorProfile'
import { useHistory } from 'react-router'
import { fontSize } from '@mui/system'
import { RootStateType } from '../../../redux-store/reducers'
import { NEXEDGE_MDMS_APIS, USER_ROLES } from '../../../utils/constant'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import CalendarIcon from '../../../assets/icons/CalendarIcon'
import dayjs from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { addYearsAndSubtractOneDay } from '../../../utils/utilityFunctions'
import { showError } from '../../../redux-store/actions/auth'
import { SAVING_MANDATE_SUCCESS } from '../../../redux-store/types/mandate'

const radioList = [
    {
        id: 0,
        label: 'E-Mandate',
    },
]

const BankValidation = Yup.object().shape({
    amount: Yup.string().required('Amount is required')
        .test('not-zero', 'Amount cannot be zero', (value) => {
            if (!value) return false;
            // Remove commas, trim, and check if numeric and not all zeros
            const numeric = parseFloat(value.replace(/,/g, '').trim());
            return numeric > 0;
        }),
    accountNo: Yup.string().required('Required'),
    ifscCode: Yup.string().required('Required').transform((value) => {
        return value && value.toUpperCase()
    }),
    bankName: Yup.string().nullable().required('Bank name is required').matches(/^[aA-zZ\s]+$/, "Only alphabets are allowed for this field "),
    startDate: Yup.string().nullable()
        .required('Start date is required')
        .test("checkDate", function (value, context) {
            const { path, createError } = context;
            const date = new Date(value ?? "");
            const todaysDate = new Date();
            date.setHours(0, 0, 0, 0);
            todaysDate.setHours(0, 0, 0, 0);

            if (isNaN(date.getTime())) {
                return createError({
                    message: 'Invalid Start Date',
                    path
                })
            }
            if (date < todaysDate) {
                return createError({
                    message: 'Start date should be greater than or equal to today',
                    path
                })
            }

            return true;
        }),
    endDate: Yup.string().nullable()
        .required('End date is required')
        .test("checkDate", function (value, context) {
            const { path, parent, createError } = context;
            const endDate = new Date(value ?? "");
            const startDate = new Date(parent?.startDate);
            endDate.setHours(0, 0, 0, 0);
            startDate.setHours(0, 0, 0, 0);

            const fortyYrsFromNow = dayjs(startDate).add(40, 'year').subtract(1, 'month').format("YYYY-MM-DD")

            if (isNaN(endDate.getTime())) {
                return createError({
                    message: 'Invalid End Date',
                    path
                })
            }
            if (dayjs(endDate).format("YYYY-MM-DD") < (dayjs(startDate).format("YYYY-MM-DD"))) {
                return createError({
                    message: 'End date should be greater than or equal to start date',
                    path
                })
            }
            if (dayjs(endDate).format("YYYY-MM-DD") > fortyYrsFromNow) {
                return createError({
                    message: 'End date should not exceed maximum of 479 months from start date',
                    path
                })
            }
            return true
        })
})


interface Values {
    amount: string;
    accountNo: string;
    ifscCode: string;
    bankName: string;
    startDate?: string;
    endDate?: string;
    acctType?: string;
}
export default function MandateCreationForm(props: any): JSX.Element {
    const { clientCode = "", onClose, handleCreateMandateStatus, prefilledValues, mandateCreationLoading = false, comingFromSysPlan = false } = props;
    const theme = useTheme();
    const dispatch = useDispatch()
    const { location: { pathname } } = useHistory()


    const [bankMandateDetails, setBankMandateDetailsRes] = useState<Array<MandateCreation>>([]);
    const [bankAccountNumbers, setBankNumber] = useState<Array<{ label: string, value: string }>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [dialogPopup, setDialogPopup] = useState<boolean>(false);
    const [registerMandateRes, setRegisterMandateRes] = useState({} as GetMandateRegisteredResponseBody);
    const [selectedRadio, setSelectedRadio] = useState<number>(radioList[0].id);
    const { selectedPortfolio } = useSelector((state: RootStateType) => state.selectedPortfiolio)
    const { clientInterfaceCode } = useSelector((state: RootStateType) => state.id)
    const { role = '' } = useSelector((state: RootStateType) => state.auth);
    const [accountType, setAccountType] = useState<string>('');

    const initialData = {
        amount: `${prefilledValues?.pending?.amount ?? '100000'}`,
        accountNo: '',
        ifscCode: '',
        bankName: '',
        startDate: dayjs().format("YYYY-MM-DD"),
        endDate: dayjs().add(40, 'year').subtract(1, 'month').format("YYYY-MM-DD"),
        acctType: ""
    }

    const { handleChange, values, handleSubmit, handleBlur, setFieldValue, setValues, errors, validateOnChange, validateField } = useFormik({
        initialValues: !prefilledValues?.active?.mandateId ? prefilledValues?.pending ?? {} : initialData,
        onSubmit: handleFormSubmit,
        validationSchema: BankValidation,
        validateOnBlur: false,
        validateOnChange: false,
        enableReinitialize: true
    })
    const defaultEndingDate = addYearsAndSubtractOneDay(values.startDate, 40)
    const savedMandateInfo = useSelector((state: RootStateType) => state.savedMandateInfo);


    const maxStartDatePossible = '2060-01-01'
    const fortyYrsFromNow = dayjs(values.startDate).add(40, 'year').subtract(1, 'month').format("YYYY-MM-DD");


    useEffect(() => {
        const fetchApi = async () => {

            try {
                setLoading(true);

                if (values?.ifscCode !== "") {
                    setValues({
                        ...values,
                        amount: prefilledValues?.pending?.amount ?? '100000',
                        accountNo: values?.accountNo,
                        ifscCode: values?.ifscCode,
                        bankName: values?.bankName,
                        startDate: dayjs(values?.startDate).format("YYYY-MM-DD"),
                        endDate: dayjs(values?.endDate).format("YYYY-MM-DD"),
                    })
                    setBankNumber(() => (
                        [
                            {
                                label: values?.accountNo,
                                value: values?.accountNo
                            }
                        ]
                    ))
                }
                else {
                    const res = await (dispatch(getMandateCreationDetails({ clientCode: clientCode, portfolioId: selectedPortfolio?.value })) as unknown as Promise<Array<MandateCreation>>);

                    const bankMandateDetailsRes: GetMandateCreationDetailsResponseBody = {
                        "mandateCreationDetails": res
                    }
                    setBankMandateDetailsRes(bankMandateDetailsRes?.mandateCreationDetails);
                    setFieldValue('ifscCode', bankMandateDetailsRes?.mandateCreationDetails[0]?.ifscCode)
                    setFieldValue('bankName', bankMandateDetailsRes?.mandateCreationDetails[0]?.bankName)
                    setFieldValue('accountNo', bankMandateDetailsRes?.mandateCreationDetails[0]?.bankAccountNumber)
                    setAccountType(bankMandateDetailsRes?.mandateCreationDetails[0]?.bankAccountType || '');
                    setFieldValue('acctType', bankMandateDetailsRes?.mandateCreationDetails[0]?.bankAccountType || '');

                    setBankNumber(() => {
                        return bankMandateDetailsRes?.mandateCreationDetails?.map((item) => {
                            return {
                                label: item?.bankAccountNumber,
                                value: item?.bankAccountNumber
                            }
                        })
                    })

                    setFieldValue('startDate', values.startDate);
                    setFieldValue('endDate', values.endDate);
                }
            } catch (e) {
                console.log("Error", e);
            } finally {
                setLoading(false);
            }
        }
        fetchApi();
    }, [])

    const handleModalDropdown = (name: string, value: string) => {
        setFieldValue("accountNo", value.toString());
        const filteredAccountNumbers = bankMandateDetails?.filter((account) => parseInt(account.bankAccountNumber) === parseInt(value));
        setFieldValue('bankName', filteredAccountNumbers?.[0]?.bankName)
        setFieldValue('ifscCode', filteredAccountNumbers?.[0]?.ifscCode);
        setAccountType(filteredAccountNumbers?.[0]?.bankAccountType || '');
        setFieldValue('acctType', filteredAccountNumbers?.[0]?.bankAccountType || '');
    };


    async function handleFormSubmit(value: Values) {
        if (dayjs(value.endDate).isBefore(dayjs(value.startDate))) {
            dispatch(showError('End date should be greater than or equal to start date'));
            return;
        }
        else if (dayjs(value.endDate).isAfter(fortyYrsFromNow)) {
            dispatch(showError('End Date exceeds maximum duration of 379 months'));
            return;
        }

        const registerMandatePayload = {
            amount: value?.amount?.toString(),
            actype: value?.acctType ?? "",
            mandateType: selectedRadio === 1 ? "X" : "N",
            clientCode: clientInterfaceCode ? clientInterfaceCode : clientCode,
            ifscCode: value?.ifscCode,
            accountNo: value?.accountNo,
            bankName: value?.bankName,
            portfolioId: selectedPortfolio?.value,
            startDate: dayjs(value?.startDate).format("MM/DD/YYYY"),
            endDate: dayjs(value?.endDate).format("MM/DD/YYYY"),
        }


        handleCreateMandateStatus(value, registerMandatePayload);
        onClose();
        // try {
        //     setLoading(true);
        //     const res = await (dispatch(getMandateRegistered(registerMandatePayload)) as unknown as Promise<GetMandateRegisteredResponseBody>);
        //     setRegisterMandateRes(res);
        //     if (res?.is_mandate_registered) {
        //         setDialogPopup(true);
        //     }
        // } catch (e) {
        //     console.log("E", e);
        // } finally {
        //     setLoading(false);
        // }
    }

    // const handleOnChange = (name: string, value: string | number) => {
    //     setFieldValue(name, value);
    //     if (name === 'startDate') {
    //         setValues({
    //             ...values,
    //             startDate: dayjs(value).format("YYYY-MM-DD"),
    //             endDate: dayjs(value).add(40, 'year').subtract(1, 'month').format("YYYY-MM-DD"),
    //         })
    //     }
    //     setTimeout(() => {
    //         validateField(name)
    //     }, 500);
    // }
    const handleOnChange = (name: string, value: string | number) => {
        if (name === 'startDate') {
            const newStartDate = dayjs(value).format("YYYY-MM-DD");
            const newEndDate = dayjs(value).add(40, 'year').subtract(1, 'month').format("YYYY-MM-DD");

            setValues((prev) => ({
                ...prev,
                startDate: newStartDate,
                endDate: newEndDate,
            }));

            // Validate both startDate and endDate after update
            setTimeout(() => {
                validateField('startDate');
                validateField('endDate');
            }, 0);
        } else {
            setFieldValue(name, value);
            setTimeout(() => {
                validateField(name);
            }, 0);
        }
    };



    return (
        <>
            <Grid container columnSpacing={4} rowSpacing={'45px'} padding={'15px 0'}>
                <Grid item lg={6} md={12} sm={12} xs={12}>
                    <CustomeTextField label="Mandate Limit" symbol={true} type={'number'} name={'amount'} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('amount', e.target.value)} value={values.amount} handleBlur={handleBlur} error={errors.amount} onKeyDown={(e) => {
                        if (['-', '+', 'e', 'E'].includes(e.key)) {
                            e.preventDefault();
                        }
                    }} />
                </Grid>
                <Grid item lg={6} md={12} sm={12} xs={12}>
                    <CustomeModalDropDown name='accountNo' label="Bank Account Number" onChange={handleModalDropdown} data={bankAccountNumbers || []} value={values.accountNo} />
                </Grid>
                <Grid item lg={6} md={12} sm={12} xs={12}>
                    <CustomeTextField label="IFSC Code" type={'text'} name={'ifsc'} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('ifsc', e.target.value)} value={values.ifscCode} handleBlur={handleBlur} error={errors.ifscCode} disabled={true} />
                </Grid>
                <Grid item lg={6} md={12} sm={12} xs={12}>
                    <CustomeTextField label="Bank Name" type={'text'} name={'bankName'} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('bankName', e.target.value)} value={values.bankName} handleBlur={handleBlur} error={errors.bankName} disabled={true} />
                </Grid>

                {/* Date Additions */}
                {/* Start Date */}
                <Grid item lg={6} md={12} sm={12} xs={12}>
                    <CustomStartDateField value={values.startDate} handleOnChange={handleOnChange} error={errors.startDate} disabled={true} />
                </Grid>

                {/* End Date */}

                <Grid item lg={6} md={12} sm={12} xs={12}>
                    <CustomEndDateField value={values.endDate} startDate={values.startDate} handleOnChange={handleOnChange} error={errors.endDate} />
                </Grid>
            </Grid>

            <Box marginTop={'25px'} gap={'25px'} display={'flex'} flexDirection={'column'}>
                <Box>
                    <Typography variant='subtitle_400' color={theme.palette.text.secondary} fontSize={{ lg: '16px', md: '16px', sm: '14px', xs: '14px' }}>
                        <Typography variant='body_500' fontWeight={600} color={theme.palette.primary[500]} component={'span'}>Note: </Typography>
                        {selectedRadio === radioList?.[0].id ? `This is a Digital Mandate requiring the investor to use Netbanking Facility. They'll receive a link for a 1 Rupee payment from their bank account. Verification takes 2 working days. List of Allowed Banks is below.` : 'This is a Physical Mandate that requires printing, signing, and scanning. Verification with the bank usually takes 21 working days.'}
                    </Typography>
                </Box>
                {selectedRadio === radioList?.[0].id && (
                    <Box display={'flex'} gap={'10px'} alignItems={'center'}>
                        <PdfIcon />
                        <Typography
                            variant='overline_600'
                            color={theme.palette.primary[500]}
                            role="link"
                            tabIndex={0}
                            aria-label="List of allowed banks (opens PDF in new tab)"
                            sx={{
                                textTransform: 'capitalize',
                                cursor: 'pointer',
                                '&:focus-visible': {
                                    outline: `2px solid ${theme.palette.primary[500]}`,
                                    outlineOffset: '2px',
                                    borderRadius: '4px',
                                },
                            }}
                            onClick={() => window.open((
                                NEXEDGE_MDMS_APIS ?
                                    "https://thrivewmsuat.kfintech.com/api/mdms/list_of_allowed_banks.pdf"
                                    : "http://wmsdev.kfintech.com/mdms/list_of_allowed_banks.pdf"), '_blank', 'noopener,noreferrer'
                            )}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    window.open((
                                        NEXEDGE_MDMS_APIS ?
                                            "https://thrivewmsuat.kfintech.com/api/mdms/list_of_allowed_banks.pdf"
                                            : "http://wmsdev.kfintech.com/mdms/list_of_allowed_banks.pdf"), '_blank', 'noopener,noreferrer');
                                }
                            }}
                        >
                            LIST OF ALLOWED BANKS
                        </Typography>

                    </Box>
                )}
                <Box>
                    <CustomeButton lookType={'contained'} btnText={comingFromSysPlan ? 'Submit' : 'Save'} isLoading={loading || mandateCreationLoading} onClick={handleSubmit} />
                </Box>
            </Box>
        </>
    )
}

type CustomeTextFieldProps = {
    label: string,
    min?: number,
    minlabel?: string,
    max?: number
    maxlabel?: string
    symbol?: boolean
    type: string
    name: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    value: string
    handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
    error?: string
    disabled?: boolean,
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}


const CssTextField = styled(TextField)(({ theme, error }) => ({
    '& label.Mui-focused': {
        color: `${error ? theme.palette.error.main : theme.palette.text.secondary}`,
        fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
    },
    '& .MuiInput-underline:after': {
        borderBottomColor: `${error ? theme.palette.error.main : theme.palette.text.secondary}80`,
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: `${error ? theme.palette.error.main : theme.palette.text.secondary}80`,
            fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
        },
        '&:hover fieldset': {
            borderColor: `${error ? theme.palette.error.main : theme.palette.text.secondary}80`,
        },
        '&.Mui-focused fieldset': {
            borderColor: `${error ? theme.palette.error.main : theme.palette.text.secondary}80`,
        },
        '& .MuiTypography-body1': {
            color: `${theme.palette.text.primary}`,
        },
        color: `${theme.palette.text.primary}`,
        fontWeight: 500,
    },
}));

const CustomeTextField = (props: CustomeTextFieldProps) => {
    const { label, min, minlabel, max, maxlabel, symbol, type, name, onChange, value, handleBlur, error, disabled, onKeyDown } = props
    const { palette } = useTheme()
    const { currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);


    return (
        <Box>
            <CssTextField
                type={type}
                name={name}
                error={typeof error === 'string'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e)}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleBlur && handleBlur(e)}
                onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
                onPaste={e => e.preventDefault()}
                value={value}
                color='primary'
                defaultValue={' '}
                size='small'
                id="outlined-basic"
                label={label}
                variant="outlined"
                disabled={disabled}
                fullWidth
                InputProps={{
                    sx: {
                        borderRadius: '10px',
                        fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' },
                    },
                    startAdornment: symbol ? <InputAdornment position="start">{String.fromCharCode(parseInt(currencySymbol, 16))}</InputAdornment> : null
                }}
                onKeyDown={onKeyDown}
            />
            {typeof error === 'string' && error !== 'Required' && (
                <Box display={'flex'} flexDirection={'row'} gap={'20px'}>
                    <Typography variant="subtitle_500" fontSize={{ lg: '14px', md: '14px', sm: '12px', xs: '12px' }} color={palette.error[500]}>{error}</Typography>
                </Box>
            )}
            {min && max && (
                <Box display={'flex'} flexDirection={'row'} gap={'20px'}>
                    <Typography variant="subtitle_500" fontSize={{ lg: '14px', md: '14px', sm: '12px', xs: '12px' }} color={palette.text.primary}>Min: {minlabel}</Typography>
                    <Typography variant="subtitle_500" fontSize={{ lg: '14px', md: '14px', sm: '12px', xs: '12px' }} color={palette.text.primary}>Max: {maxlabel}</Typography>
                </Box>
            )}
        </Box>
    )
}

const CustomStartDateField = (props: any) => {
    const { handleOnChange, error, value, disabled } = props
    const theme = useTheme()
    const { currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);


    return (
        <Box>
            <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 1.5 } }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        disabled={disabled}
                        label={"Start Date"}
                        value={dayjs(value)}
                        onChange={(date: any) => {
                            handleOnChange('startDate', date?.format("YYYY-MM-DD") ?? dayjs(new Date()).format("YYYY-MM-DD"));
                        }
                        }
                        slots={{
                            openPickerIcon: () => <CalendarIcon fillColor={disabled ? theme.palette.grey[100] : theme.palette.primary[500]} />,
                        }}
                        format="DD/MM/YYYY"
                        views={['year', 'month', 'day']}
                        minDate={dayjs(new Date())}
                        sx={{
                            '&.MuiTextField-root': {
                                '& fieldset': {
                                    borderRadius: "10px",
                                    backgroundColor: `${theme.palette.common.white}0D`,
                                    border: `0.5px solid ${theme.palette.text.secondary}80`,
                                },
                                '.MuiInputBase-input': {
                                    ...theme.typography.body_500,
                                    letterSpacing: "0.32px",
                                    color: theme.palette.text.primary,
                                    padding: "10px",
                                    '&:disabled': {
                                        cursor: 'not-allowed',
                                    }
                                },
                            },
                        }}
                    />
                </LocalizationProvider>
            </FormControl>
            {typeof error === 'string' && error !== 'Required' && (
                <Box display={'flex'} flexDirection={'row'} gap={'20px'}>
                    <Typography variant="subtitle_500" fontSize={{ lg: '14px', md: '14px', sm: '12px', xs: '12px' }} color={theme.palette.error[500]}>{error}</Typography>
                </Box>
            )}

        </Box>
    )
}

const CustomEndDateField = (props: any) => {
    const { startDate, handleOnChange, error, value } = props
    const theme = useTheme()
    const fortyYrsFromNow = dayjs(startDate).add(40, 'year').subtract(1, 'month').format("YYYY-MM-DD");
    return (
        <Box>
            <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 1.5 } }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="End Date"
                        value={dayjs(value)}
                        onChange={(date: any) => {
                            handleOnChange('endDate', date?.format("YYYY-MM-DD") ?? dayjs(new Date()).format("YYYY-MM-DD"));
                        }
                        }
                        slots={{
                            openPickerIcon: () => <CalendarIcon fillColor={theme.palette.primary[500]} />,
                        }}
                        maxDate={dayjs(fortyYrsFromNow)}
                        format="DD/MM/YYYY"
                        views={['year', 'month', 'day']}
                        sx={{
                            '&.MuiTextField-root': {
                                '& fieldset': {
                                    borderRadius: "10px",
                                    backgroundColor: `${theme.palette.common.white}0D`,
                                    border: `0.5px solid ${theme.palette.text.secondary}80`,
                                },
                                '.MuiInputBase-input': {
                                    ...theme.typography.body_500,
                                    letterSpacing: "0.32px",
                                    color: theme.palette.text.primary,
                                    padding: "10px"
                                },
                            },
                        }}
                    />
                </LocalizationProvider>
            </FormControl>
            {typeof error === 'string' && error !== 'Required' && (
                <Box display={'flex'} flexDirection={'row'} gap={'20px'}>
                    <Typography variant="subtitle_500" fontSize={{ lg: '14px', md: '14px', sm: '12px', xs: '12px' }} color={theme.palette.error[500]}>{error}</Typography>
                </Box>
            )}
        </Box>
    )
}

